/* eslint-disable */
import React, { useState, useMemo, useEffect } from 'react';
import { CheckPicker, Button, CheckboxGroup, Checkbox, InputNumber } from 'rsuite';
import { useMultiplePo } from './contexts/MultiplePoContext';
import { BaseModal } from '../BaseModal';
import { getHigherUnitAndQuantity, getHigherUnitName } from '@/Helpers';
import PoTableMultiple from '../../POTableMultiple/poTable';
import { RefreshCw, ExternalLink, Trash2, Check, X } from 'lucide-react';
import { urls } from '@/Constants';
import { SelectPicker } from 'rsuite';
import moment from 'moment';

const AddProductModal = ({ isOpen, onClose, onAdd }) => {
    const { getProductData, batches, tableData, refreshIngredients, state, workerAllocations } = useMultiplePo();
    const [step, setStep] = useState(1);
    const [selectedVariantIds, setSelectedVariantIds] = useState([]);
    const [variantqtys, setVariantqtys] = useState({});
    const [frequencies, setFrequencies] = useState({});
    const [productSelections, setProductSelections] = useState({});
    const [repeatDays, setRepeatDays] = useState({});

    const dropDownProducts = useMemo(() => {
        const excludeVariantIds = tableData?.rows?.map((item) => item?._variantId);
        return getProductData?.filter(item =>
            !excludeVariantIds?.includes(item?.variantId) &&
            ['PRODUCT', 'UNPACKED_PRODUCT'].includes(item?.skuType)
        )?.map(product => ({
            label: product.label,
            value: product.variantId,
        }));
    }, [getProductData, tableData?.rows]);


    useEffect(() => {
        refreshIngredients(state?.kitchen, true)
    }, []);

    // Initialize qtys when selection changes
    useEffect(() => {
        const newqtys = { ...variantqtys };
        selectedVariantIds.forEach(id => {
            if (newqtys[id] === undefined) {
                newqtys[id] = '';
            }
        });
        // Cleanup unselected
        Object.keys(newqtys).forEach(id => {
            if (!selectedVariantIds.includes(Number(id))) {
                delete newqtys[id];
            }
        });
        setVariantqtys(newqtys);
    }, [selectedVariantIds]);

    const selectedProducts = useMemo(() => {
        return selectedVariantIds.map(id => getProductData.find(p => p.variantId === id)).filter(Boolean);
    }, [selectedVariantIds, getProductData]);

    const step1TableData = useMemo(() => {
        const headers = [
            { name: 'Item Name', id: 'itemName', width: 200, fixed: true },
            { name: 'SKU Code', id: 'skuCode', width: 120, fixed: true },
            { name: 'Batch Size', id: 'batchSize', width: 120 },
            { name: 'Total Qty', id: 'qty', width: 120 },
            { name: 'Actions', id: 'actions', width: 60 }
        ];

        const rows = selectedProducts.map(product => {
            const batchInfo = batches?.find(b => b.variantId === product.variantId);
            const batchVal = batchInfo?.batchSize;
            const qty = variantqtys[product.variantId];

            const [displayBatch] = getHigherUnitAndQuantity(product.unit, batchVal, false);

            return {
                ...product,
                itemName: product.viewOnlyLabel,
                skuCode: product.clientVariantId || '-',
                batchSize: displayBatch,
                qty: qty,
                _batchRaw: batchVal,
            };
        });

        return { headers, rows };
    }, [selectedProducts, batches, variantqtys]);

    const renderStep1Cell = (rowData, rowIndex, header) => {
        if (header.id === 'itemName') {
            return <div className="text-left font-medium px-2 whitespace-normal break-words">{rowData.itemName}</div>;
        }
        if (header.id === 'skuCode') {
            return <div className="text-gray-500">{rowData.skuCode}</div>;
        }
        if (header.id === 'batchSize') {
            if (!rowData._batchRaw) {
                return (
                    <Button
                        appearance="link"
                        size="xs"
                        className="text-red-500 p-0 flex items-center gap-1"
                        onClick={() => window.open(`${urls.EXTENSION}${urls.VARIANT_BATCH_SIZE}?productId=${encodeURIComponent(rowData.productId)}&variantId=${encodeURIComponent(rowData.variantId)}&storeId=${encodeURIComponent(state?.kitchen)}&productName=${encodeURIComponent(rowData.itemName)}`)}
                    >
                        Add Batch Size <ExternalLink size={12} />
                    </Button>
                );
            }
            return <div>{rowData.batchSize} {getHigherUnitName(rowData.unit)}</div>;
        }
        if (header.id === 'qty') {
            const qtyVal = rowData.qty;
            const batchVal = rowData.batchSize;
            const isInvalidMultiple = qtyVal > 0 && batchVal > 0 && (qtyVal * 1000) % (batchVal * 1000) !== 0;
            const isBatchMissing = !rowData._batchRaw || rowData._batchRaw === 0;
            return (
                <div className='flex flex-col items-center justify-center gap-1'>
                    <input
                        type='number'
                        className={`text-center w-[100px] border focus:border-blue-500 outline-none transition-colors duration-200 rounded px-2 py-1 text-[12px] ${isInvalidMultiple ? 'border-red-500' : 'border-gray-300'
                            } ${isBatchMissing ? 'bg-gray-100 cursor-not-allowed border-gray-200' : ''}`}
                        value={rowData.qty ?? ''}
                        disabled={isBatchMissing}
                        onChange={(e) => {
                            const val = e.target.value;
                            setVariantqtys(prev => ({
                                ...prev,
                                [rowData.variantId]: val
                            }));
                        }}
                        onKeyDown={(e) => ['-', '+', "'", 'e', 'E'].includes(e.key) && e.preventDefault()}
                        placeholder={isBatchMissing ? 'N/A' : 'Enter qty'}
                    />
                    {isBatchMissing ? (
                        <span className='text-red-500 text-[10px] whitespace-nowrap font-medium'>
                            Batch size not defined
                        </span>
                    ) : isInvalidMultiple && (
                        <span className='text-red-500 text-[10px]'>
                            Multiple of {rowData?.batchSize} {rowData?.batchSize && getHigherUnitName(rowData?.unit)}
                        </span>
                    )}
                </div>
            );
        }
        if (header.id === 'actions') {
            return (
                <div className="flex justify-center items-center w-full h-full">
                    <Trash2
                        size={18}
                        className="text-red-400 hover:text-red-600 cursor-pointer transition-colors"
                        onClick={() => {
                            setSelectedVariantIds(prev => prev.filter(id => id !== rowData.variantId));
                        }}
                    />
                </div>
            );
        }
        return null;
    };

    const dateHeaders = useMemo(() =>
        tableData?.headers?.filter((h) => String(h.id).startsWith('date_')) || [],
        [tableData.headers]
    );

    const isDateDisabled = (dateId) => {
        const dateStr = dateId.split('_')[1];
        const isPast = moment(dateStr).isBefore(moment(), 'day');
        const isHoliday = !!workerAllocations?.[dateStr]?.isHoliday;
        return isPast || isHoliday;
    };

    const step2TableData = useMemo(() => {
        const headers = [
            { name: 'S.No', id: 'sno', width: 50, fixed: true },
            { name: 'Product Name', id: 'itemName', width: 200, fixed: true },
            { name: 'Frequency', id: 'frequency', width: 150 },
            ...dateHeaders.map(h => ({ ...h, width: 60 }))
        ];

        const productsWithQty = selectedProducts.filter(p => Number(variantqtys[p.variantId]) > 0);
        const rows = productsWithQty.map((p, idx) => ({
            ...p,
            sno: idx + 1,
            itemName: p.viewOnlyLabel,
            variantId: p.variantId,
            increaseHeight: frequencies[p.variantId] !== 'daily',
        }));

        return { headers, rows };
    }, [selectedProducts, variantqtys, dateHeaders, frequencies]);

    const handleFrequencyChange = (variantId, val) => {
        setFrequencies(prev => ({ ...prev, [variantId]: val }));
        if (val === 'daily') {
            const availableDays = dateHeaders
                .map(h => h.id)
                .filter(id => !isDateDisabled(id));
            setProductSelections(prev => ({ ...prev, [variantId]: availableDays }));
        } else if (val === 'repeat') {
            // Initialize repeat days if not set
            if (!repeatDays[variantId]) {
                setRepeatDays(prev => ({ ...prev, [variantId]: 2 }));
            }
            // Clear selections, user needs to pick a starting date
            setProductSelections(prev => ({ ...prev, [variantId]: [] }));
        } else if (val === 'custom') {
            // Keep existing selections
        }
    };

    const handleRepeatDaysChange = (variantId, val) => {
        setRepeatDays(prev => ({ ...prev, [variantId]: val }));
        // If there are already selections, recompute repeat from first selected date
        const currentSelections = productSelections[variantId] || [];
        if (currentSelections.length > 0) {
            const sortedIds = dateHeaders.map(h => h.id).filter(id => currentSelections.includes(id));
            if (sortedIds.length > 0) {
                applyRepeatPattern(variantId, sortedIds[0], val);
            }
        }
    };

    const applyRepeatPattern = (variantId, startDateId, repeatInterval) => {
        const sortedDateIds = dateHeaders.map(h => h.id);
        const startIndex = sortedDateIds.indexOf(startDateId);
        if (startIndex === -1) return;

        const interval = (parseInt(repeatInterval) || 2) + 1;
        const selected = [];

        // Go backward from start
        for (let i = startIndex; i >= 0; i -= interval) {
            const dateId = sortedDateIds[i];
            if (!isDateDisabled(dateId)) selected.push(dateId);
        }
        // Go forward from start
        for (let i = startIndex + interval; i < sortedDateIds.length; i += interval) {
            const dateId = sortedDateIds[i];
            if (!isDateDisabled(dateId)) selected.push(dateId);
        }
        setProductSelections(prev => ({ ...prev, [variantId]: selected }));
    };

    const toggleDaySelection = (variantId, dateId) => {
        if (isDateDisabled(dateId)) return;
        const freq = frequencies[variantId] || 'custom';

        if (freq === 'daily') return; // Daily mode: can't toggle individual days

        if (freq === 'repeat') {
            // In repeat mode, clicking a date sets it as the starting date
            const interval = repeatDays[variantId] || 2;
            applyRepeatPattern(variantId, dateId, interval);
            return;
        }

        // Custom mode
        setProductSelections(prev => {
            const current = prev[variantId] || [];
            if (current.includes(dateId)) {
                return { ...prev, [variantId]: current.filter(id => id !== dateId) };
            } else {
                return { ...prev, [variantId]: [...current, dateId] };
            }
        });
    };

    const handleBulkAction = (variantId, action) => {
        if (action === 'all') {
            const availableDays = dateHeaders
                .map(h => h.id)
                .filter(id => !isDateDisabled(id));
            setProductSelections(prev => ({ ...prev, [variantId]: availableDays }));
        } else {
            setProductSelections(prev => ({ ...prev, [variantId]: [] }));
        }
        setFrequencies(prev => ({ ...prev, [variantId]: 'custom' }));
    };

    const renderStep2Cell = (rowData, rowIndex, header) => {
        if (header.id === 'sno') {
            return <div className="flex w-full h-full items-center justify-center">{rowData.sno}</div>;
        }
        if (header.id === 'itemName') {
            return (
                <div className="flex w-full h-full items-center justify-center">
                    <div className="text-left px-2">
                        <div className="font-medium text-[11px] break-words">{rowData.itemName}</div>
                    </div>
                </div>
            );
        }
        if (header.id === 'frequency') {
            const freq = frequencies[rowData.variantId] || 'custom';
            const hasSelectedDate = (productSelections[rowData.variantId]?.length || 0) > 0;
            return (
                <div className='flex flex-col gap-1 items-center justify-center px-1 w-full'>
                    {freq === 'repeat' && !hasSelectedDate && (
                        <div
                            className='text-blue-500 text-[10px] font-semibold text-center'
                            style={{ animation: 'pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}
                        >
                            Select a starting<br />date to repeat
                        </div>
                    )}
                    <SelectPicker
                        data={[
                            { label: 'Daily', value: 'daily' },
                            { label: 'Repeat After', value: 'repeat' },
                            { label: 'Custom', value: 'custom' },
                        ]}
                        searchable={false}
                        cleanable={false}
                        size="xs"
                        value={freq}
                        onChange={(val) => handleFrequencyChange(rowData.variantId, val)}
                        className="w-full min-w-[100px]"
                        menuClassName='z-[999999]'
                    />
                    {freq === 'repeat' && (
                        <input
                            type='number'
                            className='w-full text-center border border-gray-300 rounded px-1 py-0.5 text-[11px] focus:border-blue-500 outline-none transition-colors'
                            value={repeatDays[rowData.variantId] ?? 2}
                            onChange={(e) => handleRepeatDaysChange(rowData.variantId, e.target.value)}
                            onKeyDown={(e) => ['-', '+', "'", 'e', 'E'].includes(e.key) && e.preventDefault()}
                            min={1}
                            placeholder='Interval'
                        />
                    )}
                    {freq === 'custom' && (
                        <div className='flex items-center gap-1'>
                            <button
                                onClick={() => handleBulkAction(rowData.variantId, 'all')}
                                className='px-2 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-600 text-[10px] font-bold hover:bg-blue-100 transition-colors shadow-sm'
                            >
                                Select All
                            </button>
                            <button
                                onClick={() => handleBulkAction(rowData.variantId, 'none')}
                                className='px-2 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-600 text-[10px] font-bold hover:bg-blue-100 transition-colors shadow-sm'
                            >
                                DeSelect All
                            </button>
                        </div>
                    )}
                </div>
            );
        }
        if (String(header.id).startsWith('date_')) {
            const isDisabled = isDateDisabled(header.id);
            const isSelected = productSelections[rowData.variantId]?.includes(header.id);

            return (
                <div className="flex w-full h-full items-center justify-center">
                    <div
                        className={`w-5 h-5 flex items-center justify-center rounded border transition-colors duration-200 ${isDisabled
                            ? 'bg-gray-100 border-gray-300 cursor-not-allowed'
                            : isSelected
                                ? 'bg-green-600 border-green-600 shadow-sm cursor-pointer text-white'
                                : 'bg-white border-gray-800 hover:border-blue-400 cursor-pointer'
                            }`}
                        onClick={() => !isDisabled && toggleDaySelection(rowData.variantId, header.id)}
                    >
                        {isDisabled ? (
                            <X size={14} className='text-gray-400' strokeWidth={3} />
                        ) : isSelected && (
                            <Check size={16} strokeWidth={3} />
                        )}
                    </div>
                </div>
            );
        }
        return null;
    };

    const handleNext = () => setStep(2);
    const handleBack = () => setStep(1);

    const isNextDisabled = useMemo(() => {
        const rows = step1TableData.rows;
        if (rows.length === 0) return true;

        const hasQty = rows.some((row) => row?.qty && row.qty !== '');
        if (!hasQty) return true;
        const isInvalid = rows.some((row) => {
            const qty = parseFloat(row.qty);
            const batch = parseFloat(row.batchSize);
            return (
                qty > 0 &&
                batch > 0 &&
                (qty * 1000) % (batch * 1000) !== 0
            );
        });
        return isInvalid;
    }, [step1TableData]);

    const handleAdd = () => {
        const productsToAdd = selectedProducts.map(p => {
            const qty = Number(variantqtys[p.variantId]);
            const selectedDaysForProduct = productSelections[p.variantId] || [];
            const batchInfo = batches?.find(b => b.variantId === p.variantId);
            const [displayBatch] = getHigherUnitAndQuantity(p.unit, batchInfo?.batchSize || 0, false);

            return {
                product: p,
                quantity: qty,
                batch: displayBatch,
                selectedDays: selectedDaysForProduct,
            };
        }).filter(p => p.quantity > 0 && p.selectedDays.length > 0);

        if (productsToAdd.length === 0) return;

        onAdd(productsToAdd);

        // Reset
        setSelectedVariantIds([]);
        setVariantqtys({});
        setFrequencies({});
        setProductSelections({});
        setRepeatDays({});
        setStep(1);
    };

    const renderFooter = () => (
        <div className="flex justify-between w-full items-center p-4 border-t border-gray-100 bg-gray-50 rounded-b-lg">
            <div>
                {step === 2 && (
                    <Button onClick={handleBack} appearance="subtle" className="font-semibold">
                        Back to Selection
                    </Button>
                )}
            </div>
            {step === 1 ? (
                <Button
                    onClick={handleNext}
                    appearance="primary"
                    className="bg-teal-600 hover:bg-teal-700 text-white px-8"
                    disabled={isNextDisabled}
                >
                    Next: Select Days
                </Button>
            ) : (
                <Button
                    onClick={handleAdd}
                    appearance="primary"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-8"
                    disabled={!selectedProducts.some(p => (productSelections[p.variantId]?.length || 0) > 0)}
                >
                    Add {selectedProducts.filter(p => (productSelections[p.variantId]?.length || 0) > 0).length} Product(s) to Plan
                </Button>
            )}
        </div>
    );

    return (
        <BaseModal
            open={isOpen}
            handleClose={onClose}
            heading={step === 1 ? "Add Products: Select & Qty" : "Add Products: Select Days"}
            width="95vw"
            height='75vw'
            renderBtns={renderFooter}
        >
            <div className="flex flex-col gap-4 p-2 h-full overflow-hidden">
                {step === 1 ? (
                    <div className="flex flex-col gap-4 h-full overflow-hidden">
                        <div className="flex flex-col gap-2 flex-shrink-0">
                            <div className="flex justify-between items-center">
                                <label className="text-[13px] font-bold text-gray-700 uppercase tracking-wider">
                                    Select Products ({selectedVariantIds.length})
                                </label>
                                <Button
                                    size="xs"
                                    appearance="subtle"
                                    onClick={() => refreshIngredients(state?.kitchen, true)}
                                    className="flex items-center gap-1 text-teal-600 hover:text-teal-700"
                                >
                                    <RefreshCw size={14} /> Refresh Batches
                                </Button>
                            </div>
                            <CheckPicker
                                data={dropDownProducts}
                                value={selectedVariantIds}
                                onChange={setSelectedVariantIds}
                                block
                                searchable
                                placeholder="Search and select multiple products..."
                                className="w-full"
                                menuClassName='z-[999999]'
                                virtualized
                            />
                        </div>

                        {selectedVariantIds.length > 0 && (
                            <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm flex-1">
                                <PoTableMultiple
                                    tableData={step1TableData}
                                    renderCellData={renderStep1Cell}
                                    headerHeight={50}
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex flex-col gap-4 h-full overflow-hidden">
                        <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 flex-shrink-0">
                            <p className="text-xs text-indigo-700 font-medium text-center">
                                Select production days for each product. Quantity will be distributed equally among selected days.
                                <br />
                                <span className="text-[10px] opacity-75 font-normal">Past dates and holidays are automatically disabled.</span>
                            </p>
                        </div>

                        <div className="border border-gray-200 rounded-lg shadow-sm flex-1 overflow-y-auto">
                            <PoTableMultiple
                                tableData={step2TableData}
                                renderCellData={renderStep2Cell}
                                headerHeight={60}
                            />
                        </div>
                    </div>
                )}
            </div>
        </BaseModal>
    );
};

export default AddProductModal;
